import {AppBar, IconButton, Toolbar, withWidth, Button} from '@material-ui/core';
import {WithWidth} from '@material-ui/core/withWidth';
import MenuIcon from '@material-ui/icons/Menu';
import StandardLogo from 'Core/assets/images/StandardLogo.svg';
import AccountMenu from 'Core/components/Navigation/AccountMenu';
import SearchBar, {mapStateToProps} from 'Core/components/Navigation/SearchBar';
import Sidebar from 'Core/components/Navigation/Sidebar';
import React, {useState} from 'react';
import {ReducerState} from 'rootReducer';
import {RouteComponentProps, withRouter} from 'react-router';
import './CollapsedMenuBar.sass';
import * as E from 'fp-ts/lib/Either';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import AdministrationMenu from './AdministrationMenu';
import {Loaded, toEither} from 'Core/models/Async';
import {Link} from 'react-router-dom';
import {useSelector, connect, useDispatch} from 'react-redux';
import {getUserScopesUserProfile} from 'Users/selectors/userProfileSelectors';
import {pipe} from 'fp-ts/lib/pipeable';
import {Scope} from 'Shared/constants/Scope';
import {AsyncUserClaims, UserClaims, UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {AgentClaims} from 'Users/models/AgentClaims';
import {changeAnalyticsState} from 'Core/ducks/analytics';
import {ANALYTICS_EVENT_ACTION} from '../Analytics/AnalyticsEventConstants';

type CollapsedMenuBarProps = ReturnType<typeof mapStateToProps> & {
  userClaims: AsyncUserClaims;
} & RouteComponentProps &
  WithWidth;

const MobileHamburgerMenu = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  return (
    <>
      <IconButton
        id="hamburger-menu-icon"
        className="collapsed-menu-bar__hamburger-menu"
        onClick={() => {
          setMenuOpen(true);
        }}
      >
        <MenuIcon color="inherit" />
      </IconButton>
      <Sidebar
        isOpen={isMenuOpen}
        onSidebarClose={() => {
          setMenuOpen(false);
        }}
      />
    </>
  );
};

export const CollapsedMenuBar = (props: CollapsedMenuBarProps) => {
  const state = useSelector((state: ReducerState) => state.user.profile);
  const dispatch = useDispatch();
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const {BR_Footer} = useSelector((state: ReducerState) => state.featureFlag);
  const {areSearchOptionsEmpty} = useSelector((state: ReducerState) => state.search);
  const {location, userClaims, width} = props;
  const isMobileView = ['xs', 'sm', 'md'].includes(width);
  const data = toEither(userClaims);

  const scopes: Scope[] = pipe(
    data,
    E.map(value => new Loaded(value)),
    E.fold(
      () => [],
      value => getUserScopesUserProfile(state, value.Data)
    )
  );
  const claims: UserClaims | undefined = pipe(
    data,
    E.map(value => new Loaded(value)),
    E.fold(
      () => undefined,
      value => value.Data
    )
  )!;
  const isImpersonating = claims.userTypes
    .map(
      UserTypeClaims.match({
        [UserTypeClaimKey.AgentSite]: (agentClaims: AgentClaims) =>
          agentClaims.impersonatedAgentClaim! ? true : false,
        default: () => false,
      })
    )
    .some(Boolean);
  const isLineOfBusinessRoot = location.pathname === '/';

  return (
    <AppBar className="collapsed-menu-bar" position="static">
      <Toolbar className="collapsed-menu-bar__toolbar">
        {isMobileView && <MobileHamburgerMenu />}
        <div className="collapsed-menu-bar__main-logo-container">
          <img
            src={StandardLogo}
            id="collapsed-menu-logo"
            onClick={() => {
              dispatch(
                changeAnalyticsState({
                  page: analytics.page,
                  ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.ALLSTATE_LOGO},
                })
              );
              props.history.push('/');
            }}
            className="collapsed-menu-bar__main-logo"
            alt="Standard Logo"
          />
        </div>
        {!isMobileView && !areSearchOptionsEmpty ? <SearchBar variant="collapsed" /> 
            : <div style={{display: 'flex', width: '100%'}}></div>}
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
          {!isImpersonating && !isLineOfBusinessRoot && (
            <div className="collapsed-menu-bar__navigation-link">
              <AdministrationMenu scopes={scopes} />
            </div>
          )}
          {!BR_Footer && (
            <div className="collapsed-menu-bar__navigation-link">
              <Link to={CoreRouteConstants.LEGAL_DOCUMENTS} className="legal">
                <Button id="administration-menu-button" className="my-access-menu" variant="text">
                  Legal
                </Button>
              </Link>
            </div>
          )}
          <AccountMenu />
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default withWidth()(withRouter(connect(mapStateToProps)(CollapsedMenuBar)));
